import React, { useEffect, useRef } from "react";
import { toJS } from "mobx";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import deviceStore from "../../../stores/DeviceStore";

const MapTab = () => {
  const mapContainer = useRef(null);
  const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;
  const theme = useTheme();

  const geocodeAddress = async ({ street, city, state }) => {
    try {
      const address = encodeURIComponent(`${street}, ${city}, ${state}`);
      const response = await fetch(
        `https://api.maptiler.com/geocoding/${address}.json?key=${MAPTILER_KEY}`
      );
      const data = await response.json();

      if (data?.features?.[0]?.center) {
        const [longitude, latitude] = data.features[0].center;
        return { latitude, longitude };
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
    return null;
  };

  useEffect(() => {
    if (!MAPTILER_KEY) {
      console.error("Missing MapTiler key");
      return;
    }

    const styleUrl =
      theme.palette.mode === "dark"
        ? `https://api.maptiler.com/maps/streets-dark/style.json?key=${MAPTILER_KEY}`
        : `https://api.maptiler.com/maps/streets/style.json?key=${MAPTILER_KEY}`;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: styleUrl,
      center: [-95.7129, 37.0902], // USA center
      zoom: 4,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    // Add device markers
    const addMarkers = async () => {
      const devices = toJS(deviceStore.devices);
      if (!Array.isArray(devices)) return;

      for (const device of devices) {
        const { location, name } = device;
        if (
          !location ||
          !location.city ||
          !location.street ||
          !location.state
        ) {
          console.warn("Invalid location for device:", name);
          continue;
        }

        const coords = await geocodeAddress(location);
        if (!coords) {
          console.warn("Could not geocode location for:", name);
          continue;
        }

        new maplibregl.Marker({ color: "red" })
          .setLngLat([coords.longitude, coords.latitude])
          .setPopup(
            new maplibregl.Popup().setHTML(`
              <div style="font-size: 14px;">
                <strong>${name}</strong><br/>
                ${location.street}<br/>
                ${location.city}, ${location.state}
              </div>
            `)
          )
          .addTo(map);
      }
    };

    addMarkers();

    return () => map.remove();
  }, [MAPTILER_KEY, theme.palette.mode]);

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography
          variant="h6"
          mb={2}
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            textAlign: "center",
          }}
        >
          Device Map Overview
        </Typography>
        <Box
          ref={mapContainer}
          sx={{
            height: "600px",
            width: "100%",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        />
      </Paper>
    </Box>
  );
};

export default MapTab;
